<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>

      <!-- Threat Spider Info Banner -->
      <div class="threat-spider-banner mb-6">
        <div class="banner-content">
          <v-icon class="banner-icon">mdi-spider</v-icon>
          <div class="banner-text">
            <h3 class="banner-title">Threat Modeling Tool</h3>
            <p class="banner-description">
              This page displays projects imported from
              <a
                :href="`http://${serverUrl}:5000`"
                target="_blank"
                rel="noopener noreferrer"
                class="threat-spider-link"
              >
                Threat Spider
              </a>
              - an integrated threat modeling and risk assessment platform.
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-6">
        <Header :text="$tc('Threat Spider Projects')" />

        <!-- Threat Spider Projects Guide -->
        <v-card class="mb-6 guide-card" outlined>
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
            <span class="title font-weight-bold">Threat Spider Projects Guide</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <p class="text-body-1 mb-3">
              In this page you can view all projects created in Threat Spider. Follow the workflow
              below to create a Subject of Certification based on Threat Spider requirements.
            </p>

            <v-row>
              <v-col cols="12" md="6">
                <div class="status-box">
                  <div class="status-header">
                    <v-icon color="info" class="mr-2">mdi-sitemap</v-icon>
                    <span class="subtitle-1 font-weight-bold">Project Navigation</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="status-description">
                    <ul class="feature-list">
                      <li>View all Threat Spider projects in one centralized location</li>
                      <li>
                        Click <v-icon small>mdi-spider</v-icon> to edit the project in Threat Spider
                      </li>
                      <li>
                        Click <v-icon small>mdi-arrow-right-bold</v-icon> to view the project's
                        components
                      </li>
                      <li>From components, view applicable requirements for each component</li>
                    </ul>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="status-box">
                  <div class="status-header">
                    <v-icon color="success" class="mr-2">mdi-certificate</v-icon>
                    <span class="subtitle-1 font-weight-bold">Create Subject of Certification</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="status-description">
                    <ul class="feature-list">
                      <li>
                        Select a requirement from a component to create a Subject of Certification
                      </li>
                      <li>The system will redirect you to the Subject of Certification form</li>
                      <li>Selected requirements will be automatically included in scope</li>
                    </ul>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <div class="p-4 rounded shadow bg-secondary-100">
          <div>
            <div class="flex flex-row items-center space-x-4">
              <v-select
                v-model="searchField"
                :items="searchFields"
                item-text="text"
                item-value="value"
                :label="$t('search-field')"
                hide-details
                outlined
                dense
                class="mb-4 search-field"
                style="max-width: 200px;"
              ></v-select>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                :label="$t('search')"
                hide-details
                class="mb-4 flex-grow-1"
                outlined
                dense
                @keyup.enter="applySearch"
              />
              <v-btn color="primary" class="mb-4" @click="handleImport">
                <v-icon left>mdi-upload</v-icon>
                Import
              </v-btn>
            </div>

            <v-data-table
              :headers="getHeaders"
              :items="projects"
              :search="search"
              :custom-filter="customFilter"
              class="elevation-1 certifai-table"
              :loading="loading"
              :items-per-page="5"
              :footer-props="{
                showFirstLastPage: true,
                firstIcon: 'mdi-arrow-collapse-left',
                lastIcon: 'mdi-arrow-collapse-right',
                prevIcon: 'mdi-arrow-left',
                nextIcon: 'mdi-arrow-right',
                disableItemsPerPage: false,
                itemsPerPageText: 'Projects per page',
                showCurrentPage: true,
              }"
              must-sort
            >
              <template v-slot:[`item.Progress`]="{ item }">
                <v-chip :color="getStatusColor(item.Progress)" outlined dark>
                  {{ item.Progress }}
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      small
                      class="mr-2"
                      @click="handleProjectClickThreatSpider(item)"
                    >
                      mdi-spider
                    </v-icon>
                  </template>
                  <span>{{ $t('Edit in Threat Spider') }}</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      small
                      class="mr-2"
                      @click="handleProjectClick(item)"
                    >
                      mdi-arrow-right-bold
                    </v-icon>
                  </template>
                  <span>{{ $t('View Components') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import util from '@/mixins/util';
import Header from '@/components/helpers/Header';
import Loader from '@/components/helpers/Loader.vue';
import EventBus from '@/helpers/event-bus.js';
import ThreatSpiderApi from '../api/threatSpiderApi';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'ThreatSpiderProjects',
  components: {
    Header,
    Loader,
    Breadcrumbs,
  },
  mixins: [util],
  computed: {
    getHeaders() {
      return [
        {
          text: 'ID',
          value: 'ID',
          class: 'text text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: this.$t('Title'),
          value: 'Title',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: this.$t('Description'),
          value: 'Description',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '20%',
        },
        {
          text: 'Progress',
          value: 'Progress',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: this.$t('soc.headers.actions'),
          value: 'actions',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: 'ID', value: 'ID' },
        { text: this.$t('Title'), value: 'Title' },
        { text: this.$t('Description'), value: 'Description' },
        { text: 'Progress', value: 'Progress' },
      ];
    },
  },
  data() {
    return {
      serverUrl: 'localhost',
      loading: false,
      search: '',
      searchField: 'all',
      projects: [],
    };
  },
  created() {
    this.fetchServerUrl();
    this.fetchProjects();
  },
  methods: {
    handleImport() {
      // Placeholder: file format and import flow are still to be defined
      EventBus.$emit('snackbar', {
        text: 'Import is not available yet.',
        color: 'info',
      });
    },
    async fetchServerUrl() {
      try {
        const response = await ThreatSpiderApi.getServerUrl();
        this.serverUrl = response.data.serverUrl;
      } catch (error) {
        console.error('Failed to fetch server URL:', error);
      }
    },
    async fetchProjects() {
      try {
        this.loading = true;
        const response = await ThreatSpiderApi.getProjectsFromThreatSpider();
        this.projects = response.data;
      } catch (error) {
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    getStatusColor(status) {
      switch (status) {
        case 'Started':
          return 'primary';
        case 'Completed':
          return 'success';
        case 'In Progress':
          return 'warning';
        default:
          return 'grey';
      }
    },
    handleProjectClick(item) {
      this.$router.push({
        name: 'ThreatSpiderComponents',
        query: { projectId: item.ID.toString() },
      });
    },
    handleProjectClickThreatSpider(item) {
      // Open external URL in a new tab
      window.open(`http://${this.serverUrl}:5000/manage_tm_project/${item.ID}`, '_blank');
    },
    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip action column and non-string values
          if (key === 'actions' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },
    applySearch() {
      // This method can be used for any additional search logic if needed
    },
  },
};
</script>
<style>
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}

.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

.threat-spider-banner {
  background: linear-gradient(135deg, #cc5522 0%, #b86f11 50%, #cc9900 100%);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(102, 51, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.threat-spider-banner:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 48px rgba(102, 51, 0, 0.4);
}

.banner-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  text-align: center;
}

.banner-icon {
  font-size: 40px !important;
  color: white !important;
  flex-shrink: 0;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25));
}

.banner-text {
  flex: 1;
}

.banner-title {
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 6px 0;
  letter-spacing: -0.5px;
}

.banner-description {
  color: rgba(255, 255, 255, 0.98);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
  font-weight: 400;
}

.threat-spider-link {
  color: white;
  text-decoration: none;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.35);
  padding: 4px 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: inline-block;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.threat-spider-link:hover {
  background: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.7);
}

.search-field {
  min-width: 150px;
}

.guide-card {
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  border-left: 4px solid #1976d2;
}

.status-box {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  height: 100%;
  transition: transform 0.2s;
}

.status-box:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-3px);
}

.status-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.status-description {
  margin-top: 8px;
}

.feature-list {
  margin: 0;
  padding-left: 16px;
  margin-top: 8px;
}

.feature-list li {
  position: relative;
  padding-left: 24px;
  margin-bottom: 8px;
  line-height: 1.5;
}

.feature-list li:before {
  content: '✔';
  position: absolute;
  left: 0;
  top: 0;
  color: var(--v-primary-base);
  font-weight: bold;
}
</style>
